import { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';

import { FormControl, FormHelperText } from '@mui/material';
import {
  DatePicker,
  type DatePickerProps,
  type PickersShortcutsItem,
} from '@mui/x-date-pickers-pro';
import { addDays, addMonths } from 'date-fns';
import { Calendar } from 'lucide-react';
import {
  type Control,
  Controller,
  type FieldError,
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
} from 'react-hook-form';

import { formFieldLucideIconProps } from '@modules/_shared/components/form/formFieldLucideIcons';
import { RaLabel } from '@modules/_shared/components/form/RaLabel';
import { type Translate, useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';

const getDateShortcuts = (t: Translate): PickersShortcutsItem<Date>[] => [
  {
    label: t('Tomorrow'),
    getValue: () => addDays(new Date(), 1),
  },
  {
    label: t('In 2 days'),
    getValue: () => addDays(new Date(), 2),
  },
  {
    label: t('In 1 week'),
    getValue: () => addDays(new Date(), 7),
  },
  {
    label: t('In 1 month'),
    getValue: () => addMonths(new Date(), 1),
  },
  {
    label: t('In 3 months'),
    getValue: () => addMonths(new Date(), 3),
  },
  {
    label: t('In 6 months'),
    getValue: () => addMonths(new Date(), 6),
  },
];

export type RaDateProps = Omit<
  DatePickerProps,
  'value' | 'onChange' | 'variant' | 'size' | 'InputProps' | 'name'
> & {
  control: Control<any>;
  name: string;
  label: string;
  required?: boolean;
  enableShortcuts?: boolean;
};

type RaDateFieldProps = Omit<RaDateProps, 'control' | 'name'> & {
  value: any;
  onChange: (
    event: ChangeEvent | FieldPathValue<FieldValues, FieldPath<FieldValues>>,
  ) => void;
  error?: FieldError;
  required?: boolean;
};

const RaDateField: React.FC<RaDateFieldProps> = ({
  label,
  required = false,
  value,
  error,
  onChange,
  enableShortcuts = false,
  ...dateFieldProps
}) => {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const shortcuts = useMemo(
    () => (enableShortcuts ? getDateShortcuts(t) : undefined),
    [enableShortcuts, t],
  );

  const dateValue = useMemo(() => {
    if (value == null) {
      return null;
    }

    try {
      // Date string without time, we have to set the time to 00:00:00 otherwise the date will have an hour shift because of timezone.
      if (typeof value === 'string' && !value.includes('T')) {
        return new Date(`${value}T00:00:00`);
      }

      return new Date(value);
    } catch (error) {
      console.error('date parsing error:', error);
      return new Date();
    }
  }, [value]);

  const handleChange = (date: Date | null) => {
    try {
      onChange(date?.toISOString() ?? null);
    } catch (error) {
      console.error('error', error);
    }
  };

  // Refocus input when picker opens to keep focus on input field
  useEffect(() => {
    if (open) {
      const timeout = setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timeout);
    }
  }, [open]);

  const handleInputClick = () => {
    setOpen(!open);
  };

  return (
    <FormControl
      fullWidth
      error={!!error}
      sx={{
        '& .MuiPickersInputBase-root': {
          height: 37,
          borderRadius: '8px',
          fontSize: '0.875rem',
          background: 'var(--mui-palette-background-paper)',
          '.dark &': {
            background: 'rgba(255, 255, 255, 0.05)',
          },
        },
        '& .MuiPickersOutlinedInput-notchedOutline': {
          borderRadius: '8px',
        },
      }}
    >
      <RaLabel label={label} required={required} />
      <DatePicker
        value={dateValue}
        onChange={handleChange}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        slots={{
          openPickerIcon: () => (
            <Calendar
              {...formFieldLucideIconProps}
              color={customPalette.gray500}
            />
          ),
        }}
        slotProps={{
          textField: {
            variant: 'outlined',
            size: 'small',
            onClick: handleInputClick,
            inputRef,
            // disable autocomplete and autofill
            slotProps: { htmlInput: { autoComplete: 'one-time-code' } },
            sx: {
              '&.MuiPickersInputBase-root': {
                height: 37,
                borderRadius: '8px',
                fontSize: '0.875rem',
              },
              '& .MuiPickersOutlinedInput-notchedOutline': {
                borderRadius: '8px',
              },
            },
          },
          shortcuts:
            shortcuts != null
              ? {
                  items: shortcuts,
                  changeImportance: 'set',
                }
              : undefined,
          layout: {
            sx: {
              '& .MuiPickersLayout-shortcuts': {
                gridArea: '2 / 1 / 3 / 2',
                paddingTop: 6,
              },
              '& .MuiPickersLayout-contentWrapper': {
                gridArea: '2 / 2 / 3 / 3',
              },
            },
          },
        }}
        {...dateFieldProps}
      />
      <FormHelperText>{error?.message}</FormHelperText>
    </FormControl>
  );
};

export const RaDate: React.FC<RaDateProps> = ({
  control,
  name,
  label,
  required = false,
  ...dateFieldProps
}) => {
  const { t } = useLocale();
  const rules = {
    required: required ? t('This field is required') : undefined,
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ fieldState: { error }, field: { onChange, value } }) => (
        <RaDateField
          label={label}
          required={required}
          value={value}
          onChange={onChange}
          error={error}
          {...dateFieldProps}
        />
      )}
    />
  );
};
